import React from 'react'
import { Info, AlertTriangle, Lightbulb } from 'lucide-react'

// Shared building blocks for the Guide pages. Older guides still carry their
// own copy of Section; new ones use these.

export function Section({ number, title, children }) {
  return (
    <section className="space-y-4" data-section-number={number}>
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{number}</span>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {children}
    </section>
  )
}

export function GuideHeader({ title, intro }) {
  return (
    <div className="space-y-2">
      <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
      {intro && <p className="text-sm text-gray-600 dark:text-gray-400">{intro}</p>}
    </div>
  )
}

export function P({ children }) {
  return <p className="text-sm text-gray-600 dark:text-gray-400">{children}</p>
}

// A name as it appears on screen: a button, tab, menu item or field.
export function UI({ children }) {
  return (
    <span className="inline-block rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-1.5 py-px text-xs font-semibold text-gray-800 dark:text-gray-200">
      {children}
    </span>
  )
}

export function Steps({ children }) {
  return <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-600 dark:text-gray-400">{children}</ol>
}

export function Bullets({ children }) {
  return <ul className="list-disc pl-5 space-y-1.5 text-sm text-gray-600 dark:text-gray-400">{children}</ul>
}

const CALLOUTS = {
  info: { Icon: Info, box: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800', icon: 'text-blue-600 dark:text-blue-400', text: 'text-blue-800 dark:text-blue-200' },
  tip: { Icon: Lightbulb, box: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800', icon: 'text-green-600 dark:text-green-400', text: 'text-green-800 dark:text-green-200' },
  warn: { Icon: AlertTriangle, box: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800', icon: 'text-amber-600 dark:text-amber-400', text: 'text-amber-800 dark:text-amber-200' },
}

export function Callout({ kind = 'info', title, children }) {
  const c = CALLOUTS[kind] || CALLOUTS.info
  const { Icon } = c
  return (
    <div className={`rounded-xl border p-4 ${c.box}`}>
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${c.icon}`} />
        <div className={`text-xs leading-relaxed ${c.text}`}>
          {title && <p className="font-semibold mb-0.5">{title}</p>}
          {children}
        </div>
      </div>
    </div>
  )
}

// Marks which roles a section applies to, e.g. <RoleTag roles={['Approver']} />.
export function RoleTag({ roles }) {
  return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {roles.map((r) => (
        <span key={r} className="rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 text-[10px] font-semibold">
          {r}
        </span>
      ))}
    </span>
  )
}

// Small framed "screenshot" used by the hand-built mockups.
export function Frame({ children, caption }) {
  return (
    <figure className="space-y-1.5">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">{children}</div>
      {caption && <figcaption className="text-[11px] text-gray-400 dark:text-gray-500">{caption}</figcaption>}
    </figure>
  )
}

export function Table({ head, rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 dark:bg-gray-800/60">
          <tr>
            {head.map((h) => (
              <th key={h} className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className="px-3 py-2 align-top text-gray-600 dark:text-gray-400">{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
